import {useContext, useMemo} from 'react';
import {query, where} from 'firebase/firestore';
import {useCollection} from 'react-firebase-hooks/firestore';
import {getShadowAssignmentsCollectionRef} from '@cip/core/firebase/client';
import type {ShadowAssignmentDocument} from '@cip/core/firebase';
import {ProfileContext} from 'contexts/ProfileContext';
import {useCourseId} from 'hooks/router/useUrlParams';

/** One of the current reviewer's assignments plus its Firestore document id. */
export interface ShadowAssignmentSummary extends ShadowAssignmentDocument {
  /** Firestore document id (`{reviewerUid}__{sectionId}__{sectionWeek}`). */
  id: string;
}

/** Return shape of {@link useShadowAssignments}. */
export interface ShadowAssignmentsResult {
  /** True while the underlying Firestore subscription is loading. */
  isLoading: boolean;
  /** The current reviewer's shadow assignments for this course. */
  assignments: ShadowAssignmentSummary[];
  /** Number of assignments (claimed + submitted). */
  assignedCount: number;
  /** Number of assignments whose feedback has been submitted. */
  submittedCount: number;
  /** True when there is at least one assignment and all are submitted. */
  isComplete: boolean;
}

/**
 * Subscribes to the current user's shadow assignments for the current course.
 * Firestore rules permit the read for `reviewerUid == auth.uid`.
 *
 * @param options.isEnabled - When false, nothing is read and the results are
 *   empty. Omitted means the read runs.
 * @returns The current assignments and derived counts. Re-renders on change;
 *   returns empty results (and logs) if the read is denied or the user/course
 *   is not yet resolved.
 */
export function useShadowAssignments({
  isEnabled = true,
}: {isEnabled?: boolean} = {}): ShadowAssignmentsResult {
  const {userData} = useContext(ProfileContext);
  const courseId = useCourseId();
  const uid = userData?.id;

  const q = useMemo(() => {
    if (!isEnabled || !uid || !courseId) return null;
    return query(
      getShadowAssignmentsCollectionRef({courseId}),
      where('reviewerUid', '==', uid),
    );
  }, [isEnabled, uid, courseId]);

  const [snapshot, isLoading, error] = useCollection(q);

  if (error) {
    console.error('[shadowing] useShadowAssignments error', error);
  }

  const assignments = useMemo<ShadowAssignmentSummary[]>(() => {
    if (!snapshot) return [];
    return snapshot.docs.map(d => ({id: d.id, ...d.data()}));
  }, [snapshot]);

  const assignedCount = assignments.length;
  const submittedCount = assignments.filter(
    a => a.status === 'submitted',
  ).length;

  return {
    isLoading,
    assignments,
    assignedCount,
    submittedCount,
    isComplete: assignedCount > 0 && submittedCount === assignedCount,
  };
}
